import { Button as AriaButton, Input as AriaInput, NumberField as AriaNumberField } from 'react-aria-components';
import styled from 'styled-components';
import { colors } from '@karnott/colors';
import { fontFamily, msDuration, pixelSize, pixelSpacing } from '@karnott/theme';

type Props = {
  /** The current value */
  value: number;
  /** Called with the new value when incremented, decremented or typed */
  setValue: (value: number) => void;
  /** A unit displayed after the value (e.g. `km/h`) */
  suffix?: string;
  /** The minimum allowed value */
  min?: number;
  /** The maximum allowed value */
  max?: number;
};

/** A numeric input with decrement and increment buttons */
export function Stepper({ value, setValue, suffix, min = 0, max = Infinity }: Props) {
  return (
    <NumberField minValue={min} maxValue={max} value={value} onChange={setValue}>
      <Button slot="decrement" aria-label="Decrement" data-testid="stepper-decrement-button">
        -
      </Button>
      <InputContainer>
        <Input data-testid="stepper-input" />
        {suffix ? <Suffix>{suffix}</Suffix> : null}
      </InputContainer>
      <Button slot="increment" aria-label="Increment" data-testid="stepper-increment-button">
        +
      </Button>
    </NumberField>
  );
}

const NumberField = styled(AriaNumberField)`
  display: flex;
  align-items: stretch;
  border: 1.5px solid ${colors('grey', 400)};
  border-radius: 8px;
  overflow: hidden;
`;

const InputContainer = styled.div`
  border-left: 1px solid ${colors('grey', 300)};
  border-right: 1px solid ${colors('grey', 300)};
  flex: 1;
  min-width: 0;
  background-color: ${colors('white')};
  display: flex;
  align-items: center;
  gap: ${pixelSpacing('small')};
  justify-content: center;
  padding-inline: ${pixelSpacing()};
`;

const Input = styled(AriaInput)`
  all: unset;
  width: 100%;
  min-width: 0;
  text-align: center;
  padding-block: ${pixelSpacing('medium')};
  font-weight: bold;
  font-size: ${pixelSize('large')};
  color: ${colors('black', 600)};
`;

const Suffix = styled.span`
  color: ${colors('grey')};
  font-size: ${pixelSize('small')};
  white-space: nowrap;
`;

const Button = styled(AriaButton)`
  all: unset;
  display: flex;
  align-items: center;
  padding: ${pixelSpacing()};
  padding-block: ${pixelSpacing('small')};
  cursor: pointer;
  background-color: ${colors('grey', 200)};
  color: ${colors('black')};
  transition: background-color ${msDuration('short')} ease-in-out;
  font-size: ${pixelSize('xLarge')};
  font-family: ${fontFamily('overstatement')};

  &:disabled {
    cursor: not-allowed;
    color: ${colors('grey')};
  }

  &:hover:not(:disabled) {
    background-color: ${colors('grey', 300)};
  }
`;
